import { useEffect, useState } from 'react';
import { CircularProgress } from '../CircularProgress';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { KPICell, SectionFrame, performanceColor } from '../scheduled/shared';
import {
  salesOrdersDashboardAPI,
  type SalesOrdersDashboardData,
  type SalesOrdersCurrentWeekData,
  type SalesOrdersDashboardParams,
} from '../../../lib/api/sales-orders-dashboard';
import type { OpenDrillDown } from './SalesOrdersShell';

const PALETTE = {
  onTime: '#0f766e',
  early:  '#2563eb',
  late:   '#dc2626',
  pending: '#cbd5e1',
};

/** Processing-stage donut: order, labels and colours (mirrors the grid Status). */
const STAGE_META: { key: string; label: string; color: string }[] = [
  { key: 'docked',    label: 'Docked',    color: '#3b82f6' },
  { key: 'wip',       label: 'WIP',       color: '#f59e0b' },
  { key: 'on_hold',   label: 'On Hold',   color: '#f97316' },
  { key: 'completed', label: 'Completed', color: '#10b981' },
];

/** Hard cap on how far back the stepper may page (mirrors backend clamp). */
const MAX_WEEK_OFFSET = 26;

interface Props {
  data: SalesOrdersDashboardData;
  formatCurrency: (n: number) => string;
  formatDate: (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;
  openDrillDown: OpenDrillDown;
  /** Feature-flag gate — when false, OTIF tile/drill-down is not rendered. */
  showOtif: boolean;
  /** Supplier-portal install dispatches goods — relabel "Received" → "Delivered". */
  isSupplierPortalMode: boolean;
  /** Active supplier/customer/part filters — mirrored on the stepper refetch. */
  filterParams: SalesOrdersDashboardParams;
  /**
   * Customer (counterparty / customer-side) viewers see ONLY the top KPI cards —
   * the chart sections below are hidden. Supplier/admin viewers keep everything.
   */
  isCustomer: boolean;
}

/** Current-week panel: Order Status + Delivery + Cost — no Quality. */
export function CurrentWeek({ data, formatCurrency, formatDate, openDrillDown, showOtif, isSupplierPortalMode, filterParams, isCustomer }: Props) {
  const deliveredLabel = isSupplierPortalMode ? 'Delivered' : 'Received';

  // ---- Week stepper ----
  // offset 0 = this week (served straight from the already-loaded payload, so
  // there's no flash on first paint). Any other offset is fetched on demand.
  const [weekOffset, setWeekOffset] = useState(0);
  const [stepperBlock, setStepperBlock] = useState<SalesOrdersCurrentWeekData | null>(null);
  const [stepperLoading, setStepperLoading] = useState(false);

  // Reset to "this week" whenever the underlying payload reloads (filters /
  // company change in the shell).
  useEffect(() => {
    setWeekOffset(0);
    setStepperBlock(null);
  }, [data]);

  // Serialise filters so the fetch effect only refires when they truly change.
  const filterKey = JSON.stringify(filterParams);

  useEffect(() => {
    if (weekOffset === 0) {
      setStepperBlock(null);
      setStepperLoading(false);
      return;
    }
    let cancelled = false;
    setStepperLoading(true);
    salesOrdersDashboardAPI
      .getCurrentWeek(weekOffset, filterParams)
      .then((resp) => { if (!cancelled) setStepperBlock(resp.data.current_week); })
      .catch(() => { if (!cancelled) setStepperBlock(null); })
      .finally(() => { if (!cancelled) setStepperLoading(false); });
    return () => { cancelled = true; };
    // filterKey captures filterParams; weekOffset drives the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset, filterKey]);

  const cw: SalesOrdersCurrentWeekData = weekOffset === 0 ? data.current_week : (stepperBlock ?? data.current_week);
  const isThisWeek = weekOffset === 0;
  const orderStatusEnd = cw.order_status_window_end ?? (() => {
    const d = new Date(`${cw.window_start}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 6);
    return d.toISOString().slice(0, 10);
  })();
  const period = {
    start: cw.window_start,
    end: cw.window_end,
    label: isThisWeek ? 'this week' : `week of ${formatDate(cw.window_start)}`,
  };
  /** Due-date window for WIP / On Hold — full calendar week Sun→Sat. */
  const orderStatusPeriod = {
    start: cw.window_start,
    end: orderStatusEnd,
    label: period.label,
  };

  const weekRangeLabel = `${formatDate(cw.window_start)} – ${formatDate(cw.window_end)}`;

  const deliveriesByDay = cw.deliveries_by_day.map((d) => ({
    day: d.day,
    onTime: d.on_time,
    early: d.early,
    late: d.late,
    pending: d.pending,
  }));

  const otdSplit = [
    { name: 'On time', value: cw.delivery.on_time, color: PALETTE.onTime, status: 'on_time' as const },
    { name: 'Early',   value: cw.delivery.early,   color: PALETTE.early,  status: 'early' as const },
    { name: 'Late',    value: cw.delivery.late,    color: PALETTE.late,   status: 'late' as const },
  ];
  const otdEmpty = otdSplit.every((d) => d.value === 0);
  const otdData = otdEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9', status: null }]
    : otdSplit;

  // ---- Status split donut (PO lines by processing stage) ----
  const statusSplitRaw = cw.status_split || {};
  const statusSplit = STAGE_META
    .map((m) => ({ name: m.label, key: m.key, value: statusSplitRaw[m.key] || 0, color: m.color }))
    .filter((d) => d.value > 0);
  const statusEmpty = statusSplit.length === 0;
  const statusData = statusEmpty ? [{ name: 'No data', key: '', value: 1, color: '#f1f5f9' }] : statusSplit;
  const statusTotal = statusSplit.reduce((acc, d) => acc + d.value, 0);
  const wipCount = cw.order_status.wip_count ?? 0;
  const onHoldCount = cw.order_status.on_hold_count ?? 0;

  // ---- Click handlers (chart segments) ----
  const handleDayBarClick = (
    weekday: string,
    statusKey: 'on_time' | 'early' | 'late' | 'pending',
  ) => {
    if (statusKey === 'pending') {
      // Pending bars span the WHOLE week (Sun..Sat), incl. days after today, so
      // the drill-down window must reach week-end — not the cost window that
      // stops at today. The weekday filter then narrows to the clicked bar.
      const weekEndDate = new Date(`${cw.window_start}T00:00:00Z`);
      weekEndDate.setUTCDate(weekEndDate.getUTCDate() + 6);
      openDrillDown({
        scope: 'outstanding',
        period: { start: cw.window_start, end: weekEndDate.toISOString().slice(0, 10), label: period.label },
        title: `Pending deliveries — ${weekday}`,
        subtitle: 'Live lines due that day still outstanding',
        extraFilters: { weekday },
      });
      return;
    }
    // Open the WHOLE day's receipts (not just the clicked segment) — the
    // drill-down modal already splits them across All / On Time / Early / Late
    // sub-tabs. Passing on_time_status would server-filter the fetch and leave
    // the other tabs empty, so we omit it and let the modal do the split.
    openDrillDown({
      scope: 'received',
      period,
      title: `Deliveries — ${weekday}`,
      subtitle: 'Receipts that day, split by on-time outcome',
      extraFilters: { weekday },
    });
  };

  const handleOtdSliceClick = (status: 'on_time' | 'early' | 'late' | null) => {
    if (!status || otdEmpty) return;
    openDrillDown({
      scope: 'otd',
      // Full Sun→Sat week — the OTD donut measures the whole week (not just
      // up to today), so the drill-down window must match or the counts
      // diverge (donut late vs drill late).
      period: orderStatusPeriod,
      title: `${status === 'on_time' ? 'On-time' : status === 'early' ? 'Early' : 'Late'} — lines due this week`,
      subtitle: 'OTD outcome of lines due this week',
      extraFilters: { on_time_status: status },
    });
  };

  const handleStatusSliceClick = (key?: string, label?: string) => {
    if (statusEmpty || !key) return;
    const scope = ({ wip: 'wip', on_hold: 'on_hold', docked: 'docked', completed: 'completed' } as const)[
      key as 'wip' | 'on_hold' | 'docked' | 'completed'
    ];
    if (!scope) return;
    openDrillDown({
      scope,
      period: orderStatusPeriod,
      title: `${label} — ${period.label}`,
      subtitle: 'Active-stage lines due this week',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setWeekOffset((o) => Math.min(o + 1, MAX_WEEK_OFFSET))}
            disabled={weekOffset >= MAX_WEEK_OFFSET || stepperLoading}
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-[180px] text-center">
            <div className="text-sm font-semibold text-slate-900">
              {isThisWeek ? 'This Week' : `${weekOffset} week${weekOffset === 1 ? '' : 's'} ago`}
            </div>
            <div className="text-[11px] text-slate-500 tabular-nums">{weekRangeLabel}</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setWeekOffset((o) => Math.max(o - 1, 0))}
            disabled={isThisWeek || stepperLoading}
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {stepperLoading && (
          <span className="flex items-center gap-1.5 text-xs text-slate-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading week…
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Order Status" accent="red" subtitle={isThisWeek ? 'due this week' : 'due in week'}>
          <div className="grid grid-cols-2 gap-3">
            <KPICell
              label="WIP"
              value={<span className="text-2xl font-semibold tabular-nums">{wipCount.toLocaleString()}</span>}
              foot="lines due this week with Status WIP"
              onClick={() => openDrillDown({
                scope: 'wip',
                period: orderStatusPeriod,
                title: 'WIP — This Week',
                subtitle: 'Lines due this week — Status WIP',
              })}
            />
            <KPICell
              label="On Hold"
              value={<span className="text-2xl font-semibold tabular-nums">{onHoldCount.toLocaleString()}</span>}
              foot="lines due this week on hold"
              onClick={() => openDrillDown({
                scope: 'on_hold',
                period: orderStatusPeriod,
                title: 'On Hold — This Week',
                subtitle: 'Lines due this week that are held',
              })}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Delivery" accent="green" subtitle={isThisWeek ? 'Sun → today' : 'full week'}>
          <div className={`grid ${showOtif ? 'grid-cols-2' : 'grid-cols-1'} gap-3 items-start`}>
            {showOtif && (
              <KPICell
                label="OTIF"
                value={<CircularProgress percentage={cw.delivery.otif_pct} size={68} strokeWidth={6} color={performanceColor(cw.delivery.otif_pct)} />}
                foot={`${cw.delivery.otif_met}/${cw.delivery.total_due} order lines`}
                onClick={() => openDrillDown({
                  scope: 'otif_miss',
                  period,
                  title: 'OTIF Misses — This Week',
                  subtitle: 'Past-due lines that missed on-time-in-full',
                })}
              />
            )}
            <KPICell
              label="OTD"
              value={<CircularProgress percentage={cw.delivery.otd_pct} size={68} strokeWidth={6} color={performanceColor(cw.delivery.otd_pct)} />}
              foot={`${cw.delivery.otd_met}/${cw.delivery.total_due} on time`}
              onClick={() => openDrillDown({
                scope: 'late',
                period,
                title: 'OTD Misses — This Week',
                subtitle: 'Past-due lines that missed on-time delivery',
              })}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue" subtitle="open book now · delivered this week">
          <div className="grid grid-cols-3 gap-3">
            <KPICell
              label="Open Order Lines"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(data.portfolio.open_orders_value)}</span>}
              foot={`${(data.portfolio.open_lines ?? 0).toLocaleString()} open lines (not yet late)`}
              onClick={() => openDrillDown({
                scope: 'open',
                period: null,
                title: 'Open Order Lines',
                subtitle: 'Live lines still open and not yet late',
              })}
            />
            <KPICell
              label={deliveredLabel}
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.received_value)}</span>}
              foot={`of ${formatCurrency(cw.cost.total_value)} committed`}
              onClick={() => openDrillDown({
                scope: 'delivered',
                period,
                title: `${deliveredLabel} Value — This Week`,
                subtitle: 'Lines due this week that have been received',
              })}
            />
            <KPICell
              label="Arrears"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(data.portfolio.arrears_value)}</span>}
              foot={`${(data.portfolio.arrears_lines ?? 0).toLocaleString()} overdue lines${data.portfolio.arrears_value === 0 && (data.portfolio.arrears_lines ?? 0) > 0 ? ' • unpriced in source' : ''}`}
              onClick={() => openDrillDown({
                scope: 'arrears',
                period: null,
                title: 'Arrears',
                subtitle: 'All overdue lines still open',
              })}
            />
          </div>
        </SectionFrame>
      </div>

      {/* Customer (counterparty) viewers see ONLY the top KPI cards above — the
          chart sections below are supplier/admin-only. */}
      {!isCustomer && (
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Deliveries by Day</CardTitle>
            <p className="text-[11px] text-muted-foreground">Sunday through Saturday • {isThisWeek ? 'this week' : weekRangeLabel}</p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deliveriesByDay} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="onTime"  stackId="a" fill={PALETTE.onTime}  name="On time"
                    onClick={(d) => { const day = (d?.payload as { day?: string } | undefined)?.day; if (day) handleDayBarClick(day, 'on_time'); }}
                    style={{ cursor: 'pointer' }} />
                  <Bar dataKey="early"   stackId="a" fill={PALETTE.early}   name="Early"
                    onClick={(d) => { const day = (d?.payload as { day?: string } | undefined)?.day; if (day) handleDayBarClick(day, 'early'); }}
                    style={{ cursor: 'pointer' }} />
                  <Bar dataKey="late"    stackId="a" fill={PALETTE.late}    name="Late"
                    onClick={(d) => { const day = (d?.payload as { day?: string } | undefined)?.day; if (day) handleDayBarClick(day, 'late'); }}
                    style={{ cursor: 'pointer' }} />
                  <Bar dataKey="pending" stackId="a" fill={PALETTE.pending} name="Pending" radius={[3, 3, 0, 0]}
                    onClick={(d) => { const day = (d?.payload as { day?: string } | undefined)?.day; if (day) handleDayBarClick(day, 'pending'); }}
                    style={{ cursor: 'pointer' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">OTD Mix</CardTitle>
            <p className="text-[11px] text-muted-foreground">Week to date • {cw.delivery.total_with_status.toLocaleString()} order lines due</p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={otdData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={56}
                    outerRadius={84}
                    paddingAngle={otdEmpty ? 0 : 2}
                    stroke="none"
                    onClick={(d: { status?: 'on_time' | 'early' | 'late' | null }) => handleOtdSliceClick(d?.status ?? null)}
                    style={{ cursor: otdEmpty ? 'default' : 'pointer' }}
                  >
                    {otdData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Status Split</CardTitle>
            <p className="text-[11px] text-muted-foreground">Active stages (excl. Shipped/Cancelled) • {statusTotal.toLocaleString()} lines</p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={56}
                    outerRadius={84}
                    paddingAngle={statusEmpty ? 0 : 2}
                    stroke="none"
                    onClick={(d: { key?: string; name?: string }) => handleStatusSliceClick(d?.key, d?.name)}
                    style={{ cursor: statusEmpty ? 'default' : 'pointer' }}
                  >
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
}
